import { useState } from "react";
import { api, ApiError } from "../api/client";
import { MeResponse } from "../types";

/** Раздел «Стоимость для клиента» (запрос клиента).
 *  Два варианта Excel-выгрузки: за сегодня (после фикса+поручения) и
 *  накопительно с 01.06.2026. Файлы скачиваются через стандартный
 *  механизм grant (analytics_export, владелец = текущий пользователь). */
export function ClientCostPage({ me }: { me: MeResponse }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  if (!me.permissions.client_cost) {
    return (
      <div className="text-tg-hint text-sm">
        Раздел доступен бухгалтеру, финансовому руководителю и админу.
      </div>
    );
  }

  const run = async (
    kind: "today" | "total", filename: string, label: string,
  ) => {
    setBusy(kind);
    setMsg(null);
    try {
      const res = await api.post<{ id: number }>(`/api/client-cost/${kind}`, {});
      if (!res.id) {
        setMsg("Отчёт сформирован, но не найден для скачивания.");
        return;
      }
      await api.download("analytics_export", { export_id: res.id }, filename);
      setMsg(`✅ ${label} сформирован.`);
    } catch (e) {
      setMsg((e as ApiError).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <h1 className="text-xl font-semibold mb-1">📊 Стоимость для клиента</h1>
      <p className="text-xs text-tg-hint mb-3">
        Excel-выгрузка по форме клиента. В отчёт попадают заявки с фиксом
        и полученным поручением.
      </p>

      {msg && (
        <div className="mb-3 p-2 rounded-lg bg-tg-sectionBg text-sm">{msg}</div>
      )}

      <section className="bg-tg-sectionBg rounded-xl p-3 mb-3">
        <div className="text-sm font-medium mb-1">За текущий день</div>
        <div className="text-xs text-tg-hint mb-2">
          Только заявки сегодняшнего операционного дня, по которым уже
          нажат «Фикс» и получено поручение.
        </div>
        <button
          disabled={busy === "today"}
          onClick={() => run("today", "client_cost_day.xlsx",
                             "Отчёт за день")}
          className="px-4 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm font-medium disabled:opacity-50"
        >
          {busy === "today" ? "Формирую…" : "📥 Сформировать за день"}
        </button>
      </section>

      <section className="bg-tg-sectionBg rounded-xl p-3">
        <div className="text-sm font-medium mb-1">Накопительно с 01.06.2026</div>
        <div className="text-xs text-tg-hint mb-2">
          Все заявки прошлых дней + заявки текущего дня после фикса и
          получения поручения.
        </div>
        <button
          disabled={busy === "total"}
          onClick={() => run("total", "client_cost_total.xlsx",
                             "Накопительный отчёт")}
          className="px-4 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm font-medium disabled:opacity-50"
        >
          {busy === "total" ? "Формирую…" : "📥 Сформировать накопительно"}
        </button>
      </section>
    </div>
  );
}
